import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { XIcon, Trash2Icon, ArrowRightIcon } from "lucide-react";
import { useQuote } from "../contexts/QuoteContext";
import { useGoToQuote } from "../hooks/useGoToQuote";
const ease = [0.23, 1, 0.32, 1] as const;
export function QuoteDrawer() {
  const {
    items,
    isOpen,
    close,
    remove
  } = useQuote();
  const goToQuote = useGoToQuote();
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close]);
  return <AnimatePresence data-id="element-292" data-mp="3npe5ufz6obsi">
      {isOpen && <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="quote-drawer-title" data-id="element-293" data-mp="3nq7fbopj1fec">
          <motion.div className="absolute inset-0 bg-ink/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} onClick={close} data-id="element-294" data-mp="2r7007el97cvc" />
          <motion.aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-paper shadow-2xl" initial={{
        x: "100%"
      }} animate={{
        x: 0
      }} exit={{
        x: "100%"
      }} transition={{
        duration: 0.28,
        ease
      }} data-id="element-295" data-mp="30shhw9qv894c">
            <div className="flex items-start justify-between border-b border-line px-7 py-6" data-id="element-296" data-mp="2m4cz75rtbxlk">
              <div data-id="element-297" data-mp="2i1qb9fy9iwtn">
                <h2 id="quote-drawer-title" className="font-display text-3xl text-ink" data-id="element-298" data-mp="2i16rdtp1xycz">
                  Ma sélection
                </h2>
                <p className="mt-1 text-sm text-stone" data-id="element-299" data-mp="2d59fq0oqhg6j">
                  {items.length === 0 ? "Aucun ingrédient pour le moment" : `${items.length} ingrédient${items.length > 1 ? "s" : ""} à chiffrer`}
                </p>
              </div>
              <button type="button" onClick={close} className="flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-cream" aria-label="Fermer" data-id="element-300" data-mp="6dibchz8d6o2">
                <XIcon className="h-5 w-5" data-id="element-301" data-mp="1quzwbxzu1fj8" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-7" data-id="element-302" data-mp="1vtucstabus06">
              {items.length === 0 ? <div className="py-16" data-id="element-303" data-mp="2jjf8mcrkxjqv">
                  <p className="font-display text-2xl leading-snug text-olive" data-id="element-304" data-mp="2jkgmsq3w6mct">
                    Ajoutez des ingrédients depuis le catalogue pour préparer votre demande.
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-stone" data-id="element-305" data-mp="29k7kcujb44oz">
                    Vous pouvez aussi décrire directement votre besoin dans le formulaire : notre équipe
                    vous oriente vers les références adaptées.
                  </p>
                </div> : <ul data-id="element-306" data-mp="136879fhjhdao">
                  <AnimatePresence initial={false} data-id="element-307" data-mp="136gnnhcvf0qm">
                    {items.map(item => <motion.li key={item.id} layout initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.18,
                ease
              }} className="flex items-start justify-between gap-4 border-b border-line py-5" data-id="element-308" data-mp="3hbdael1n74pq">
                        <div className="min-w-0" data-id="element-309" data-mp="2tnlxbf8a2nne">
                          <p className="font-medium text-ink" data-id="element-310" data-mp="2tox0pdxt9tdz">{item.name}</p>
                          <p className="mt-1 truncate text-xs italic text-stone" data-id="element-311" data-mp="37fsacsg4weim">{item.inci}</p>
                        </div>
                        <button type="button" onClick={() => remove(item.id)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-stone hover:bg-cream hover:text-terracotta" aria-label={`Retirer ${item.name}`} data-id="element-312" data-mp="32mqtiewda4we">
                          <Trash2Icon className="h-4 w-4" data-id="element-313" data-mp="2sm7z1lpnj7lz" />
                        </button>
                      </motion.li>)}
                  </AnimatePresence>
                </ul>}
            </div>

            <div className="border-t border-line px-7 py-6" data-id="element-314" data-mp="32caq3gd7qbho">
              <button type="button" onClick={() => {
            close();
            window.setTimeout(goToQuote, 200);
          }} className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-terracotta text-sm font-medium text-paper transition-colors duration-150 ease-out hover:bg-terracotta-dark" data-id="element-315" data-mp="37aufjqr0m5es">
                {items.length === 0 ? "Décrire mon besoin" : "Finaliser ma demande de devis"}
                <ArrowRightIcon className="h-4 w-4" data-id="element-316" data-mp="1qv19r2pdrzkl" />
              </button>
              <p className="mt-3 text-center text-xs text-stone" data-id="element-317" data-mp="1jc1qcx1x6818">Réponse sous 48 h ouvrées · sans engagement</p>
            </div>
          </motion.aside>
        </div>}
    </AnimatePresence>;
}